import { useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import { AuthErrorMessage } from '../components/auth-mfa/AuthErrorMessage';
import { MfaSetupCompleteNotice } from '../components/auth-mfa/MfaSetupCompleteNotice';
import { TotpSetupForm } from '../components/auth-mfa/TotpSetupForm';
import { useWebAuth } from '../contexts/WebAuthContext';
import type { SignInFlowError } from '../hooks/classify-auth-error';
import { useAccountSecurity } from '../hooks/use-account-security';
import { getAppOpenUri } from '../services/auth-redirect';

const PRIMARY_BUTTON =
  'w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const SECONDARY_BUTTON =
  'w-full py-2 px-4 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50';

/**
 * 再登録の注意書き。認証アプリから古い登録を先に削除すると、再ログインで求められる
 * コードを確認できなくなり、管理者に MFA のリセットを依頼するしかなくなる。
 */
function KeepOldEntryNotice() {
  return (
    <div
      role="note"
      className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 text-xs text-foreground"
    >
      認証アプリから古い登録（きゅーもAI
      エージェント）を削除するのは、新しい登録が完了してからにしてください。先に削除すると、ログインに必要なコードを確認できなくなります。
    </div>
  );
}

/** 登録中・登録後の画面の名前。登録済みの認証アプリを置き換える場合だけ「再登録」とする */
function enrollmentTitle(replacing: boolean): string {
  return replacing ? '認証アプリの再登録' : '認証アプリの登録';
}

/**
 * この画面のエラー表示。想定外のエラーはサインイン向けの文言ではなく、この画面向けの文言にする
 */
function SecurityErrorMessage({ error }: { error: SignInFlowError | null }) {
  if (error?.kind === 'unknown' || error?.kind === 'unsupported') {
    return (
      <div role="alert" className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
        <p className="text-red-400 text-sm">
          処理を完了できませんでした。時間をおいてもう一度お試しください。
        </p>
      </div>
    );
  }
  return <AuthErrorMessage error={error} />;
}

/**
 * アカウントのセキュリティ（認証アプリの登録・再登録）
 *
 * デスクトップアプリのアカウントメニュー「認証アプリの登録」からブラウザで開く。
 * 未ログインなら AuthGuard がログインへ送り、ログイン後にこの画面へ戻る。
 */
export function AccountSecurityPage() {
  const { signOut } = useWebAuth();
  const security = useAccountSecurity();
  const { state } = security;
  // デスクトップアプリのメニューから開いた場合（再ログインを挟んでもクエリは引き継がれる）
  const [searchParams] = useSearchParams();
  const openedFromApp = searchParams.get('from') === 'app';

  if (state.kind === 'loading') {
    return (
      <AuthLayout title="アカウントのセキュリティ" subtitle="読み込み中...">
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-4 border-muted border-l-primary-500 rounded-full animate-spin" />
        </div>
      </AuthLayout>
    );
  }

  if (state.kind === 'reauthRequired') {
    // サインアウトすると AuthGuard がログイン（この画面へ戻るリダイレクト付き）へ送る
    return (
      <AuthLayout
        title="アカウントのセキュリティ"
        subtitle="本人確認のため、もう一度ログインしてください"
      >
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">
            認証アプリの登録には、直前 10
            分以内のログイン（認証アプリのコード入力を含む）が必要です。
          </p>
          <button type="button" onClick={() => signOut()} className={PRIMARY_BUTTON}>
            もう一度ログインする
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (state.kind === 'enrolling') {
    return (
      <AuthLayout
        title={enrollmentTitle(state.replacing)}
        subtitle={
          state.replacing ? '新しい認証アプリを登録してください' : '認証アプリを登録してください'
        }
      >
        <div className="space-y-4">
          {state.replacing && (
            <>
              <p className="text-xs text-muted-foreground">
                登録が完了するまでは、これまでの認証アプリのコードが引き続き有効です。
              </p>
              <KeepOldEntryNotice />
            </>
          )}
          <TotpSetupForm
            setupUri={state.setupUri}
            sharedSecret={state.sharedSecret}
            submitting={security.submitting}
            error={security.error}
            onSubmit={security.submitCode}
          />
          <button
            type="button"
            onClick={() => security.cancelEnrollment()}
            disabled={security.submitting}
            className={SECONDARY_BUTTON}
          >
            キャンセル
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (state.kind === 'preferencePending') {
    return (
      <AuthLayout title={enrollmentTitle(state.replacing)}>
        <div className="space-y-6">
          <SecurityErrorMessage error={security.error} />
          <p className="text-sm text-muted-foreground">
            認証アプリのコードは確認できましたが、設定の保存に失敗しました。もう一度お試しください。
          </p>
          <button
            type="button"
            onClick={() => security.retryPreference()}
            disabled={security.submitting}
            className={PRIMARY_BUTTON}
          >
            設定を再試行する
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (state.kind === 'enrolled') {
    return (
      <AuthLayout title={enrollmentTitle(state.replacing)}>
        <MfaSetupCompleteNotice
          onContinue={security.reload}
          continueLabel="戻る"
          extraAction={
            openedFromApp ? (
              <a
                href={getAppOpenUri()}
                className="block w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white text-center font-medium rounded-lg transition-colors"
              >
                アプリに戻る
              </a>
            ) : undefined
          }
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="アカウントのセキュリティ">
      <div className="space-y-6">
        <SecurityErrorMessage error={security.error} />
        <div className="flex items-center justify-between rounded-lg border border-border p-4">
          <span className="text-sm text-foreground">認証アプリ</span>
          <span className="text-sm font-medium text-foreground">
            {state.totpEnabled === null
              ? '確認できませんでした'
              : state.totpEnabled
                ? '登録済み'
                : '未登録'}
          </span>
        </div>
        {state.totpEnabled === null && (
          <button
            type="button"
            onClick={() => security.reload()}
            disabled={security.submitting}
            className={SECONDARY_BUTTON}
          >
            再読み込み
          </button>
        )}
        <button
          type="button"
          onClick={() => security.startEnrollment()}
          disabled={security.submitting}
          className={PRIMARY_BUTTON}
        >
          {state.totpEnabled === false ? '認証アプリを登録する' : '認証アプリを再登録する'}
        </button>
        {state.totpEnabled === true && <KeepOldEntryNotice />}
        <p className="text-xs text-muted-foreground">
          スマートフォンの紛失などで認証アプリを使えなくなった場合は、管理者に MFA
          のリセットを依頼してください。
        </p>
      </div>
    </AuthLayout>
  );
}
